import { animated, config, useTransition } from '@react-spring/web';
import useComponentSize from '@rehooks/component-size';
import { useRef } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';

import { Label } from '../../../cdl/Label/Label';
import { Radio, RadioGroup, RadioGroupItem } from '../../../cdl/RadioGroup/RadioGroup';
import { Tag } from '../../../cdl/Tag/Tag';
import { Text } from '../../../cdl/Text/Text';
import { translate } from '../../../common/helpers/translate.helper';
import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { OutdatedPriceTag } from '../../create-enquiry/components/OutdatedPriceTag';
import {
    SupplierSelectionPriceState,
    SupplierSelectionRowData,
} from '../../create-enquiry/components/SupplierSelection.types';

const ContractSelectionPanel = styled(animated.div)`
    margin: 0 ${theme.space[12]};
    border-right: 1px solid ${theme.colors.border.muted};
    border-bottom: 1px solid ${theme.colors.border.muted};
    border-left: 1px solid ${theme.colors.border.muted};
    border-bottom-right-radius: ${theme.radii[6]};
    border-bottom-left-radius: ${theme.radii[6]};
    overflow: hidden;
`;

const ContractRadioGroup = styled(RadioGroup)`
    gap: ${theme.space[8]};
`;

const ContractRow = styled(RadioGroupItem)<{ $selected: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 40px;
    border: 1px solid ${(props) => (props.$selected ? theme.colors.accent.border : theme.colors.border.subtle)};
    border-radius: ${theme.radii[6]};
    background: ${(props) => (props.$selected ? theme.colors.accent.background : theme.colors.background.subtle)};
    box-shadow: ${(props) => (props.$selected ? theme.shadows.small : 'none')};
    cursor: pointer;
    user-select: none;
    transition:
        background-color 100ms ease-out,
        border-color 100ms ease-out,
        box-shadow 100ms ease-out,
        transform 160ms ease-out;
    transform: scale(1);

    &:hover:not([data-selected='true']) {
        border-color: ${theme.colors.border.muted};
        background-color: ${theme.colors.background.inset};
    }
`;

const ContractNameLabel = styled(Label)`
    min-width: 0;
    flex: 1;
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.space[16]};
    padding: ${theme.space[8]} 0 ${theme.space[8]} ${theme.space[12]};
`;

interface ContractSelectionProps {
    row: SupplierSelectionRowData;
}

export const ContractSelection = ({ row }: ContractSelectionProps) => {
    const contractSelectionBodyRef = useRef<HTMLDivElement>(null);
    const { height } = useComponentSize(contractSelectionBodyRef);
    const isOpen = !!row.selected && !!row.contracts && row.contracts.length > 1 && !!row.onContractSelect;
    const contractSelectionTransition = useTransition(isOpen, {
        from: {
            opacity: 0,
            height: 0,
        },
        leave: {
            opacity: 0,
            height: 0,
        },
        update: {
            opacity: 1,
            height: height + 1,
        },
        config: {
            ...config.default,
            tension: 300,
            clamp: true,
        },
    });

    return (
        <>
            {contractSelectionTransition((style, item) => {
                if (!item || !row.contracts || !row.onContractSelect) {
                    return null;
                }

                return (
                    <ContractSelectionPanel style={style}>
                        <Box ref={contractSelectionBodyRef} padding={12}>
                            <ContractRadioGroup
                                value={row.selectedContractId ?? ''}
                                onValueChange={row.onContractSelect}
                            >
                                {row.contracts.map((contract) => {
                                    const selected = contract.id === row.selectedContractId;
                                    const contractPrice = row.contractPricesByContractId?.[contract.id];
                                    const isDefaultContract = contract.id === row.defaultContractId;

                                    return (
                                        <ContractRow key={contract.id} $selected={selected} data-selected={selected}>
                                            <ContractNameLabel data-testid={`contract-selection-row-${contract.id}`}>
                                                <Box display="flex" alignItems="center" gap={8} minWidth={0}>
                                                    <Text variant="extraSmall" fontWeight={600}>
                                                        {contract.name}
                                                    </Text>
                                                    {isDefaultContract ? (
                                                        <Tag>{translate('labelList.default')}</Tag>
                                                    ) : null}
                                                </Box>
                                                {contractPrice?.state === SupplierSelectionPriceState.LOADING ? (
                                                    <Skeleton width={80} height={16} />
                                                ) : null}
                                                {(contractPrice?.state === SupplierSelectionPriceState.AVAILABLE ||
                                                    contractPrice?.state === SupplierSelectionPriceState.UNAVAILABLE) &&
                                                contractPrice.label ? (
                                                    <Box display="flex" alignItems="center" gap={8} flexShrink={0}>
                                                        {contractPrice.isOutdated ? (
                                                            <OutdatedPriceTag validUntil={contractPrice.validUntil} />
                                                        ) : null}
                                                        <Text variant="extraSmall" fontWeight={600}>
                                                            {contractPrice.label}
                                                        </Text>
                                                    </Box>
                                                ) : null}
                                            </ContractNameLabel>

                                            <Box display="flex" alignItems="center" flexShrink={0} padding={8}>
                                                <Radio value={contract.id} />
                                            </Box>
                                        </ContractRow>
                                    );
                                })}
                            </ContractRadioGroup>
                        </Box>
                    </ContractSelectionPanel>
                );
            })}
        </>
    );
};
